import React, { useMemo } from 'react';
import { Pencil, Zap, AlertTriangle, Plus } from 'lucide-react';

/**
 * Renders a panel's circuits as a two-column breaker board — odd positions in
 * the left column, even in the right — the way a physical panel is numbered.
 * Each column is a fixed-row grid so the board reads like the real panel:
 *
 *   - Every slot from 1..totalSlots is drawn. Empty slots render as muted,
 *     dashed "+ N" placeholders that open the Add-Circuit form pre-filled with
 *     that slot (suppressed while a form is open, i.e. readOnly).
 *   - A double-pole (or quad) breaker spans TWO rows and reserves its partner
 *     slot (position + 2, same bus column) so the partner isn't a separate
 *     addable blank — mirroring the mechanical handle tie. The span is derived
 *     from pole_type; there is no stored span column.
 *   - Circuits with no position render in a separate "Unassigned" section
 *     below (never dropped).
 *
 * Two soft, non-blocking warnings (deliberately never DB/schema rejections —
 * see the Circuit model comment): a **collision** (two circuits claim the same
 * position) and an **overlap** (a double-pole's derived partner slot is itself
 * independently occupied — so the partner circuit is NOT hidden; both render).
 *
 * Props:
 *   circuits         - Array of circuit objects (from PanelResponse.circuits)
 *   totalSlots       - panel.total_slots (nullable); drives blank-slot rendering
 *   onEditCircuit    - (circuit) => void
 *   onAddAtPosition  - (position:number) => void  — blank-slot click
 *   readOnly         - when true, cells are non-interactive and blank slots are
 *                      hidden (used while an inline Add/Edit form is open).
 */

const DOUBLE_POLES = ['Double-pole', 'Quad'];
const spanFor = (circuit) => (circuit && DOUBLE_POLES.includes(circuit.pole_type) ? 2 : 1);
const roundUpEven = (n) => (n % 2 === 0 ? n : n + 1);

// Fixed row height so a 2-row span is exactly twice a single slot and the two
// columns stay aligned row-for-row. Cells clip to it.
const ROW_HEIGHT = '5rem';

const CircuitCell = ({ circuit, collision, overlap, span, onEdit, readOnly = false }) => (
    <button
        type="button"
        onClick={() => onEdit(circuit)}
        disabled={readOnly}
        style={{ gridRow: span === 2 ? 'span 2' : undefined }}
        className={`group h-full w-full text-left p-2 sm:p-3 rounded-lg border bg-[var(--bg-card)] overflow-hidden transition-all theme-transition ${
            readOnly ? 'cursor-default' : 'hover:border-[var(--primary)]'
        } ${collision || overlap ? 'border-[var(--error)]' : 'border-[var(--border)]'}`}
    >
        <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 min-w-0">
                {circuit.position != null && (
                    <>
                        {/* The number is hidden on phones to give the name room
                            (the board reads row-major in slot order), but slot
                            identity must survive for assistive tech — keep an
                            always-present sr-only label and hide the visual
                            badge from the a11y tree. */}
                        <span className="sr-only">Slot {circuit.position}</span>
                        <span aria-hidden="true" className="shrink-0 hidden sm:inline-flex items-center justify-center min-w-[1.75rem] h-7 px-1.5 rounded-md bg-[var(--bg-hover)] text-xs font-bold text-[var(--text-main)]">
                            {circuit.position}
                        </span>
                    </>
                )}
                <span className="truncate font-medium text-[var(--text-main)]" title={circuit.label}>{circuit.label}</span>
            </div>
            <Pencil
                size={14}
                className="shrink-0 text-[var(--text-muted)] opacity-0 group-hover:opacity-100 transition-opacity"
            />
        </div>
        <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-[var(--text-secondary)]">
            {circuit.max_amps != null && <span>{circuit.max_amps}A</span>}
            {circuit.pole_type && (
                <span className="px-1.5 py-0.5 rounded bg-[var(--bg-hover)]">{circuit.pole_type}</span>
            )}
            {/* "Standard" protection is the common case — hide it to cut noise; only
                surface a protection chip when it's GFCI/AFCI/Dual-function. */}
            {circuit.protection_type && circuit.protection_type !== 'Standard' && (
                <span className="px-1.5 py-0.5 rounded bg-[var(--bg-hover)]">{circuit.protection_type}</span>
            )}
            {circuit.voltage != null && <span>{circuit.voltage}V</span>}
            {collision && (
                <span className="inline-flex items-center gap-1 text-[var(--error)]">
                    <AlertTriangle size={12} /> slot reused
                </span>
            )}
            {overlap && !collision && (
                <span className="inline-flex items-center gap-1 text-[var(--error)]">
                    <AlertTriangle size={12} /> spans occupied slot
                </span>
            )}
        </div>
    </button>
);

const BlankSlot = ({ position, onAdd }) => (
    <button
        type="button"
        data-testid="blank-slot"
        onClick={() => onAdd(position)}
        style={{ gridRow: undefined }}
        className="group h-full w-full flex items-center gap-2 p-2 sm:p-3 rounded-lg border border-dashed border-[var(--border)] text-[var(--text-muted)] hover:border-[var(--primary)] hover:text-[var(--primary)] transition-all theme-transition"
    >
        {/* Same as CircuitCell: badge hidden on phones, slot number preserved
            for screen readers so "Add" isn't announced without its slot. */}
        <span className="sr-only">Slot {position}</span>
        <span aria-hidden="true" className="shrink-0 hidden sm:inline-flex items-center justify-center min-w-[1.75rem] h-7 px-1.5 rounded-md bg-[var(--bg-hover)] text-xs font-bold">
            {position}
        </span>
        <span className="inline-flex items-center gap-1 text-xs"><Plus size={12} /> Add</span>
    </button>
);

const PanelBoard = ({ circuits = [], totalSlots = null, onEditCircuit, onAddAtPosition, readOnly = false }) => {
    const { columns, unassigned, usedSlots, blankLimit, missingTotal } = useMemo(() => {
        const positioned = circuits.filter((c) => c.position != null);
        const unpositioned = circuits.filter((c) => c.position == null);

        // Circuits keyed by slot (an array per slot: >1 is a collision).
        const byPosition = {};
        for (const c of positioned) {
            (byPosition[c.position] ||= []).push(c);
        }

        const collisions = new Set();
        for (const pos of Object.keys(byPosition)) {
            if (byPosition[pos].length > 1) byPosition[pos].forEach((c) => collisions.add(c.id));
        }

        const highestOccupied = positioned.reduce((m, c) => Math.max(m, c.position), 0);
        const hasTotal = totalSlots != null && totalSlots > 0;
        // Draw blank placeholders up to here; fall back to the highest occupied
        // slot rounded up to an even number when total_slots is unknown.
        const limit = hasTotal ? totalSlots : roundUpEven(highestOccupied);
        // Never drop a circuit that sits beyond the panel's declared size.
        const renderMax = Math.max(limit, highestOccupied);

        // Reserve the empty partner slot of each double-pole (position + 2, same
        // parity/column). If the partner is independently occupied, DON'T
        // consume it — flag overlap and render both, so no real circuit vanishes.
        const consumed = new Set();
        const overlapIds = new Set();
        for (const c of [...positioned].sort((a, b) => a.position - b.position)) {
            if (spanFor(c) !== 2) continue;
            const partner = c.position + 2;
            if (partner > renderMax) continue;
            if (byPosition[partner]?.length) overlapIds.add(c.id);
            else consumed.add(partner);
        }

        const buildColumn = (parity) => {
            const cells = [];
            for (let s = parity === 'odd' ? 1 : 2; s <= renderMax; s += 2) {
                if (consumed.has(s)) continue; // covered by the span of slot s-2
                const occupants = byPosition[s];
                if (occupants?.length) {
                    // Render EVERY circuit at this slot (a collision is >1) so a
                    // second circuit sharing the slot is never dropped — only the
                    // first (primary) carries the double-pole span.
                    occupants.forEach((c, idx) => {
                        const didSpan = idx === 0 && spanFor(c) === 2 && consumed.has(s + 2);
                        cells.push({
                            kind: 'circuit',
                            circuit: c,
                            span: didSpan ? 2 : 1,
                            collision: collisions.has(c.id),
                            overlap: overlapIds.has(c.id),
                        });
                    });
                } else if (s <= limit && !readOnly) {
                    cells.push({ kind: 'blank', position: s });
                } else {
                    // Empty slot we're not drawing as an interactive blank — a
                    // hidden slot while a form is open (readOnly), or a gap below
                    // an out-of-capacity circuit. Emit an invisible SPACER so the
                    // slots that DO render keep their true grid row and the two
                    // columns stay aligned. Trailing spacers are trimmed below so
                    // they don't add empty rows at the bottom.
                    cells.push({ kind: 'spacer', slot: s });
                }
            }
            // Trim trailing spacers: they'd only pad the column with empty rows.
            while (cells.length && cells[cells.length - 1].kind === 'spacer') cells.pop();
            return cells;
        };

        return {
            columns: { left: buildColumn('odd'), right: buildColumn('even') },
            unassigned: unpositioned,
            // Footprint: count DISTINCT occupied slots (two circuits colliding on
            // one slot is one physical position, not two) plus each reserved
            // empty partner slot of a double-pole.
            usedSlots: Math.min(
                new Set(positioned.map((c) => c.position)).size + consumed.size,
                hasTotal ? totalSlots : Infinity,
            ),
            blankLimit: limit,
            missingTotal: !hasTotal,
        };
    }, [circuits, totalSlots, readOnly]);

    if (circuits.length === 0 && blankLimit === 0) {
        return (
            <div className="p-8 text-center text-[var(--text-muted)]">
                <Zap size={40} className="mx-auto mb-3 opacity-50" />
                <p>No circuits on this panel yet.</p>
            </div>
        );
    }

    const renderCell = (cell) => {
        if (cell.kind === 'spacer') {
            // Invisible placeholder that only reserves its grid row for alignment.
            return (
                <div
                    key={`spacer-${cell.slot}`}
                    data-testid="slot-spacer"
                    aria-hidden
                    className="pointer-events-none"
                />
            );
        }
        if (cell.kind === 'blank') {
            return <BlankSlot key={`blank-${cell.position}`} position={cell.position} onAdd={onAddAtPosition} />;
        }
        return (
            <CircuitCell
                key={cell.circuit.id}
                circuit={cell.circuit}
                collision={cell.collision}
                overlap={cell.overlap}
                span={cell.span}
                onEdit={onEditCircuit}
                readOnly={readOnly}
            />
        );
    };

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between text-xs text-[var(--text-secondary)]">
                <span>
                    {usedSlots} of {totalSlots ?? blankLimit} slots used
                </span>
                {missingTotal && (
                    <span className="text-[var(--text-muted)]">
                        Set total slots on this panel to map every position.
                    </span>
                )}
            </div>

            <div className="grid grid-cols-2 gap-2 sm:gap-3">
                {/* data-testid: layout is asserted by PanelBoard.test.jsx and
                    e2e/tests/houses/electrical.spec.ts — a stable hook beats
                    coupling tests to Tailwind class names. Each column is a
                    fixed-row grid so a double-pole span-2 card lines up with two
                    single slots and the columns stay row-aligned. */}
                <div data-testid="panel-column" className="grid gap-2" style={{ gridAutoRows: ROW_HEIGHT }}>
                    {columns.left.map(renderCell)}
                </div>
                <div data-testid="panel-column" className="grid gap-2" style={{ gridAutoRows: ROW_HEIGHT }}>
                    {columns.right.map(renderCell)}
                </div>
            </div>

            {unassigned.length > 0 && (
                <div>
                    <h4 className="text-xs font-semibold text-[var(--text-secondary)] uppercase mb-2">
                        Unassigned slot
                    </h4>
                    <div className="grid grid-cols-2 gap-2 sm:gap-3" style={{ gridAutoRows: ROW_HEIGHT }}>
                        {unassigned.map((c) => (
                            <CircuitCell key={c.id} circuit={c} collision={false} overlap={false} span={1} onEdit={onEditCircuit} readOnly={readOnly} />
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default PanelBoard;
